/**
 * WorkforceEventsDrawer — the staff-facing events surface behind My Day's "Book
 * event" button (which was inert until 2026-08-03, REQ-105 → REQ-127).
 *
 * Shape: a right slide-over (`<Drawer panel="events">`), per the overlay standard —
 * a list with filters and tabs is a Drawer, never a modal, and it rides the URL so
 * browser Back closes it.
 *
 * The operator's rulings, so nobody re-derives them:
 *  · Primary moment is "book for the customer I'm with NOW" — the drawer takes an
 *    optional `customer`, prefills it, and says whose seat it is booking. It also
 *    books for anyone else: the name field is editable and the drawer opens with it
 *    empty from the header button.
 *  · NOT branch-scoped: events are network-wide (a Back Bay customer may well go to
 *    a Beacon Hill evening seminar). Branch is a filter, not a boundary.
 *  · Filters are branch + category + search. Deliberately NO date picker: the list is
 *    chronological and short, and "further out" is a real `Load more` page
 *    (`nextCursor`), not a client-side slice.
 *  · Two ways to send: register the customer directly, or mint a link they complete
 *    themselves. Both are awaited ops and both record a referral.
 *  · Seats, waitlist and override are EVENT CONFIGURATION, not UI policy. A full
 *    event with a waitlist offers the waitlist; a full event whose configuration
 *    allows a staff override offers it behind a confirmation `<Modal>` (a decision,
 *    ≤480px — the ten-second test); a full event that allows neither is disabled and
 *    says why. The server enforces the same rules and answers 409 EVENT_FULL.
 *  · Referrals made is the second tab: every event referral AND every appointment
 *    handed to another advisor because the line of business is not one he serves,
 *    with what came back. Same loop-closure principle as the coaching thread.
 *
 * Ops: listMyBookableEvents · registerEventGuest · createEventRegistrationLink ·
 * listMyReferrals (REQ-127).
 */
import { useEffect, useState } from "react";
import { Tab, Chip, Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Drawer, Modal } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useBusy } from "./useWorkforce";
import {
  listMyBookableEvents, registerEventGuest, createEventRegistrationLink, listMyReferrals,
  type BookableEvent, type MyEventsResponse, type MyReferralsResponse, type StaffReferral,
} from "./wf-api";
import { dayLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const PAGE = 4;
const mono = { fontFamily: T.fontMono };
const OUTCOME_TONE: Record<StaffReferral["outcome"], "success" | "warn" | "neutral" | "danger"> = { // ui-config
  registered: "success", attended: "success", booked: "success", completed: "success",
  waitlisted: "warn", sent: "warn",
  declined: "neutral", expired: "neutral", "no-show": "danger",
};
const OUTCOME_LABEL: Record<string, { l: string; k: string }> = { // ui-config
  sent: { l: "Sent", k: "workforce.events.outSent" },
  registered: { l: "Registered", k: "workforce.events.outRegistered" },
  waitlisted: { l: "Waitlisted", k: "workforce.events.outWaitlisted" },
  attended: { l: "Attended", k: "workforce.events.outAttended" },
  booked: { l: "Booked", k: "workforce.events.outBooked" },
  completed: { l: "Completed", k: "workforce.events.outCompleted" },
  declined: { l: "Declined", k: "workforce.events.outDeclined" },
  "no-show": { l: "No-show", k: "workforce.events.outNoShow" },
  expired: { l: "Expired", k: "workforce.events.outExpired" },
};

function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return m === 0 ? `${h} ${ap}` : `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

/** Seat state, entirely from the event's own configuration. */
function seatState(e: BookableEvent, t: (k: string, f: string) => string) {
  if (e.capacity === null) return { label: t("workforce.events.openAttendance", "Open attendance"), tone: "neutral" as const, canSeat: true, canWaitlist: false };
  if (!e.full) return { label: `${e.seatsLeft} ${t("workforce.events.seatsLeft", "seats left")}`, tone: (e.seatsLeft ?? 0) <= 3 ? "warn" as const : "success" as const, canSeat: true, canWaitlist: false };
  if (e.waitlistEnabled) return { label: `${t("workforce.events.fullWaitlist", "Full · waitlist")} ${e.waitlistCount}`, tone: "warn" as const, canSeat: false, canWaitlist: true };
  return { label: t("workforce.events.full", "Full"), tone: "danger" as const, canSeat: false, canWaitlist: false };
}

function Field({ label, value, onChange, placeholder, testid }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; testid: string }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: T.n2 }}>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        style={{ height: 34, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1 }} />
    </label>
  );
}

export default function WorkforceEventsDrawer({ customer, onClose }: { customer?: string | null; onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<"events" | "referrals">("events"); // ui-local
  const [q, setQ] = useState(""); // ui-local
  const [branch, setBranch] = useState<string | null>(null); // ui-local
  const [category, setCategory] = useState<string | null>(null); // ui-local
  const [rows, setRows] = useState<BookableEvent[]>([]);
  const [meta, setMeta] = useState<MyEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refs, setRefs] = useState<MyReferralsResponse | null>(null);
  const [openId, setOpenId] = useState<string | null>(null); // ui-local (which row's booking form is open)
  const [name, setName] = useState(customer ?? ""); // ui-local
  const [note, setNote] = useState(""); // ui-local
  const [confirm, setConfirm] = useState<BookableEvent | null>(null); // ui-local (override confirmation)
  const { busy, run } = useBusy();

  /* Filters and paging are SERVER-side: every change is a fresh first page. */
  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    listMyBookableEvents({ q, branch: branch ?? undefined, category: category ?? undefined, limit: PAGE })
      .then((r) => { if (!live) return; setMeta(r); setRows(r.events); setLoading(false); })
      .catch((e: unknown) => { if (!live) return; setError(e instanceof Error ? e.message : String(e)); setLoading(false); });
    return () => { live = false; };
  }, [q, branch, category]);

  useEffect(() => {
    let live = true;
    listMyReferrals().then((r) => { if (live) setRefs(r); }).catch(() => { /* tab shows its own empty state */ });
    return () => { live = false; };
  }, []);

  const reloadReferrals = () => { listMyReferrals().then(setRefs).catch(() => { /* keep last */ }); };

  const loadMore = () => {
    if (!meta?.nextCursor) return;
    void run("more", async () => {
      try {
        const r = await listMyBookableEvents({ q, branch: branch ?? undefined, category: category ?? undefined, cursor: meta.nextCursor ?? 0, limit: PAGE });
        setRows((prev) => [...prev, ...r.events]);
        setMeta(r);
      } catch (e) { toast({ tone: "danger", title: t("workforce.events.couldntLoadMore", "Couldn't load more events"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };

  const applyResult = (updated: BookableEvent) => setRows((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));

  const doRegister = (e: BookableEvent, mode: "seat" | "waitlist" | "override") => {
    const who = name.trim();
    if (!who) { toast({ tone: "warn", title: t("workforce.events.whoIsItFor", "Who is the seat for?"), subtitle: t("workforce.events.addACustomerName", "Add a customer name first.") }); return; }
    void run(`reg:${e.id}`, async () => {
      try {
        const r = await registerEventGuest(e.id, { customerName: who, mode, note: note.trim() || undefined });
        applyResult(r.event);
        setOpenId(null); setNote(""); setConfirm(null);
        reloadReferrals();
        toast({
          tone: "success",
          title: r.registration.mode === "waitlist" ? t("workforce.events.addedToWaitlist", "Added to the waitlist") : t("workforce.events.seatBooked", "Seat booked"),
          subtitle: `${who} · ${e.title}`,
        });
      } catch (err) {
        toast({ tone: "danger", title: t("workforce.events.couldntBook", "Couldn't book that seat"), subtitle: err instanceof Error ? err.message : String(err) });
      }
    });
  };

  const doLink = (e: BookableEvent) => {
    void run(`link:${e.id}`, async () => {
      try {
        const r = await createEventRegistrationLink(e.id, { customerName: name.trim() || undefined, note: note.trim() || undefined });
        try { await navigator.clipboard.writeText(r.url); } catch { /* clipboard blocked — the toast still carries the link */ }
        reloadReferrals();
        toast({ tone: "success", title: t("workforce.events.linkCopied", "Registration link copied"), subtitle: r.url });
      } catch (err) {
        toast({ tone: "danger", title: t("workforce.events.couldntCreateLink", "Couldn't create the link"), subtitle: err instanceof Error ? err.message : String(err) });
      }
    });
  };

  const tabBtn = (id: "events" | "referrals", label: string, count?: number) => (
    <Tab key={id} active={tab === id} count={count} testid="wfevents-tab" onClick={() => setTab(id)}>{label}</Tab>
  );

  return (
    <>
      <Drawer panel="events" width={560} testid="wfevents-drawer" onClose={onClose}
        title={t("workforce.events.title", "Branch events")}
        sub={customer
          ? `${t("workforce.events.bookingFor", "Booking for")} ${customer}`
          : t("workforce.events.acrossEveryBranch", "Upcoming across every branch — book a seat or send a link")}>
        <div style={{ display: "flex", gap: 18, padding: "0 20px", borderBottom: `1px solid ${T.n6}`, flexShrink: 0 }}>
          {tabBtn("events", t("workforce.events.upcoming", "Upcoming"), meta?.total)}
          {tabBtn("referrals", t("workforce.events.referralsMade", "Referrals made"), refs?.counts.total)}
        </div>

        {tab === "events" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "16px 20px 24px" }}>
            {/* Who the seat is for. Prefilled from the appointment when the drawer was
                opened from one; editable so he can book for anybody. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "12px 14px", borderRadius: 10, background: alpha(T.p1, 4), border: `1px solid ${alpha(T.p1, 18)}` }}>
              <Field label={t("workforce.events.customer", "Customer")} value={name} onChange={setName}
                placeholder={t("workforce.events.customerNamePlaceholder", "Name on the registration")} testid="wfevents-customer" />
              <Field label={t("workforce.events.noteOptional", "Note (optional)")} value={note} onChange={setNote}
                placeholder={t("workforce.events.whyYouReSendingThem", "Why you're sending them")} testid="wfevents-note" />
            </div>

            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfevents-search"
              placeholder={t("workforce.events.searchPlaceholder", "Search events, topics, hosts…")}
              style={{ height: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1 }} />
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              <Chip selected={!branch && !category} testid="wfevents-filter-all" onClick={() => { setBranch(null); setCategory(null); }}>{t("workforce.events.all", "All")}</Chip>
              {(meta?.facets.branches ?? []).map((b) => (
                <Chip key={b} data-id={b} selected={branch === b} testid="wfevents-filter-branch" onClick={() => setBranch(branch === b ? null : b)}>{b}</Chip>
              ))}
              {(meta?.facets.categories ?? []).map((c) => (
                <Chip key={c} data-id={c} selected={category === c} testid="wfevents-filter-category" onClick={() => setCategory(category === c ? null : c)}>{c}</Chip>
              ))}
            </div>

            {error ? (
              <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.events.couldntLoad", "Couldn't load events:")} {error}</div>
            ) : loading ? (
              <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("common.status.loading", "Loading…")}</div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.events.nothingMatches", "No upcoming events match those filters.")}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {rows.map((e) => {
                  const seats = seatState(e, t);
                  const open = openId === e.id;
                  const blocked = !seats.canSeat && !seats.canWaitlist && !e.overrideAllowed;
                  return (
                    <div key={e.id} data-testid="wfevents-row" data-id={e.id}
                      style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, overflow: "hidden" }}>
                      <div style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 7 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{e.title}</span>
                          <Pill tone={seats.tone}>{seats.label}</Pill>
                        </div>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: T.n2 }}>
                          <span style={mono}>{dayLabel(e.startsAt.slice(0, 10), "wk-d")} · {timeLabel(e.startsAt)}</span>
                          {e.locationName ? <span>{e.locationName}</span> : null}
                          {e.category ? <span style={{ color: T.soft }}>{e.category}</span> : null}
                        </div>
                        {e.hostName ? <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.events.hostedBy", "Hosted by")} {e.hostName}</span> : null}
                        {e.description ? <p style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5, margin: 0 }}>{e.description}</p> : null}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                          {open ? (
                            <>
                              {seats.canSeat ? (
                                <Btn size="sm" disabled={busy !== null} testid="wfevents-book-seat" onClick={() => doRegister(e, "seat")}>
                                  {busy === `reg:${e.id}` ? "…" : t("workforce.events.bookSeat", "Book seat")}
                                </Btn>
                              ) : seats.canWaitlist ? (
                                <Btn size="sm" disabled={busy !== null} testid="wfevents-book-waitlist" onClick={() => doRegister(e, "waitlist")}>
                                  {busy === `reg:${e.id}` ? "…" : t("workforce.events.joinWaitlist", "Add to waitlist")}
                                </Btn>
                              ) : e.overrideAllowed ? (
                                <Btn size="sm" variant="secondary" disabled={busy !== null} testid="wfevents-override" onClick={() => setConfirm(e)}>
                                  {t("workforce.events.bookAnyway", "Book anyway…")}
                                </Btn>
                              ) : (
                                <span style={{ fontSize: 11.5, color: T.red1, fontWeight: 600, alignSelf: "center" }}>
                                  {t("workforce.events.fullNoWaitlist", "Full — this event takes no waitlist and no overrides")}
                                </span>
                              )}
                              <Btn size="sm" variant="secondary" disabled={busy !== null} testid="wfevents-send-link" onClick={() => doLink(e)}>
                                <Icon.link size={12} /> {busy === `link:${e.id}` ? "…" : t("workforce.events.sendLink", "Send link")}
                              </Btn>
                              <Btn size="sm" variant="ghost" onClick={() => setOpenId(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
                            </>
                          ) : (
                            <Btn size="sm" variant={blocked ? "secondary" : "primary"} testid="wfevents-open" onClick={() => setOpenId(e.id)}>
                              {t("workforce.events.book", "Book")}
                            </Btn>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {meta?.nextCursor !== null && meta?.nextCursor !== undefined ? (
                  <Btn variant="secondary" full disabled={busy !== null} testid="wfevents-load-more" onClick={loadMore}>
                    {busy === "more" ? t("common.status.loading", "Loading…") : `${t("workforce.events.loadMore", "Load more events")} (${(meta.total ?? 0) - rows.length})`}
                  </Btn>
                ) : rows.length > PAGE ? (
                  <span style={{ fontSize: 11.5, color: T.soft, textAlign: "center" }}>{t("workforce.events.thatsEverything", "That's every upcoming event.")}</span>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", padding: "4px 0 24px" }}>
            {refs ? (
              <div style={{ display: "flex", gap: 26, padding: "14px 20px 12px", flexWrap: "wrap" }}>
                {[
                  { l: t("workforce.events.landed", "Landed"), v: refs.counts.landed, c: T.green1 },
                  { l: t("workforce.events.waitingOnThem", "Waiting on them"), v: refs.counts.waiting, c: refs.counts.waiting ? T.orange1 : T.n1 },
                  { l: t("workforce.events.toEvents", "To events"), v: refs.counts.event, c: T.n1 },
                  { l: t("workforce.events.toColleagues", "To colleagues"), v: refs.counts.appointment, c: T.n1 },
                ].map((k) => (
                  <span key={k.l} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{k.l}</span>
                    <span style={{ fontSize: 21, fontWeight: 700, color: k.c, ...mono }}>{k.v}</span>
                  </span>
                ))}
              </div>
            ) : null}
            <p style={{ fontSize: 11.5, color: T.soft, margin: "0 20px 12px", lineHeight: 1.5 }}>
              {t("workforce.events.referralsExplainer", "Every customer you sent to an event, and every appointment you handed to a colleague because the line of business isn't one you serve.")}
            </p>
            {!refs || refs.referrals.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.events.noReferralsYet", "No referrals yet.")}</div>
            ) : refs.referrals.map((r, i) => {
              const meta2 = OUTCOME_LABEL[r.outcome];
              return (
                <div key={r.id} data-testid="wfevents-referral-row" data-id={r.id}
                  style={{ padding: "13px 20px", borderTop: i === 0 ? `1px solid ${T.n6}` : "none", borderBottom: `1px solid ${T.n6}`, display: "flex", flexDirection: "column", gap: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{r.customer}</span>
                    <Pill tone={OUTCOME_TONE[r.outcome] ?? "neutral"}>{meta2 ? t(meta2.k, meta2.l) : r.outcome}</Pill>
                  </div>
                  <span style={{ fontSize: 12.5, color: T.n2 }}>
                    {r.kind === "event"
                      ? <>{t("workforce.events.toEvent", "To")} <b style={{ fontWeight: 600 }}>{r.eventTitle}</b>{r.locationName ? ` · ${r.locationName}` : ""}</>
                      : <>{t("workforce.events.handedTo", "Handed to")} <b style={{ fontWeight: 600 }}>{r.toStaff}</b>{r.lob ? ` · ${r.lob}` : ""}</>}
                  </span>
                  {r.note ? <p style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, margin: 0 }}>{r.note}</p> : null}
                  <span style={{ fontSize: 11, color: T.soft, ...mono }}>
                    {dayLabel(r.sentAt, "md")} · {r.sentVia === "link" ? t("workforce.events.viaLink", "link sent")
                      : r.sentVia === "registered" ? t("workforce.events.viaRegistered", "registered by you")
                      : r.sentVia === "booked" ? t("workforce.events.viaBooked", "appointment booked")
                      : t("workforce.events.viaHandoff", "details passed on")}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>

      {/* Capacity override — a DECISION, so a Modal, not another drawer (drawers replace,
          never stack). Operator ruling: he may book past capacity but must confirm. */}
      {confirm ? (
        <Modal testid="wfevents-override-modal" onClose={() => setConfirm(null)}
          title={t("workforce.events.overrideTitle", "Book past capacity?")}
          sub={`${confirm.title} · ${confirm.registeredCount}/${confirm.capacity ?? "—"} ${t("workforce.events.registered", "registered")}`}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setConfirm(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn disabled={busy !== null} testid="wfevents-override-confirm" onClick={() => doRegister(confirm, "override")}>
                {busy === `reg:${confirm.id}` ? "…" : t("workforce.events.overrideConfirm", "Book anyway")}
              </Btn>
            </>
          }>
          <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.55, margin: 0 }}>
            {t("workforce.events.overrideBody", "This event is full. Adding")} <b>{name.trim() || t("workforce.events.thisCustomer", "this customer")}</b>{" "}
            {t("workforce.events.overrideBody2", "puts it over its configured capacity — the host will see the extra name on their list.")}
          </p>
        </Modal>
      ) : null}
    </>
  );
}
